import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AnalyticsProvider, useAnalytics } from './context/AnalyticsContext';

// Layouts
import Layout from './components/Layout/Layout';
import PublicLayout from './components/Layout/PublicLayout';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';

// Pages
import Dashboard from './pages/Dashboard/Dashboard';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import LinkCreate from './pages/Links/LinkCreate';
import LinkList from './pages/Links/LinkList';
import LinkDetails from './pages/Links/LinkDetails';
import ConversionList from './pages/Conversions/ConversionList';
import ExportData from './pages/Export/ExportData';
import AccountPage from './pages/Account/AccountPage';
import NotFound from './pages/NotFound/NotFound';
import Upgrade from './pages/Upgrade/Upgrade';
import Home from './pages/Home/Home';
import TermsPage from './pages/Legal/TermsPage';
import PrivacyPage from './pages/Legal/PrivacyPage';

// Subscription Pages
import SubscriptionPage from './pages/subscription/SubscriptionPage';
import SubscriptionSuccess from './pages/subscription/SubscriptionSuccess';
import SubscriptionCancel from './pages/subscription/SubscriptionCancel';
import InvoicesPage from './pages/subscription/InvoicesPage';
import ProFeatures from './pages/ProFeatures';
import CookieConsent from './components/CookieConsent/CookieConsent';

import './App.css';

const AppRoutes = () => {
  const { loading } = useAuth();
  // Import and use the page tracking hook
  const usePageTracker = () => {
    const location = useLocation();
    const { trackPageView } = useAnalytics();
    
    useEffect(() => {
      // Get the page name from the pathname
      const pageName = location.pathname
        .split('/')
        .filter(segment => segment)
        .join('/') || 'home';
      
      // Track the page view
      trackPageView(pageName);
    }, [location, trackPageView]);
  };

  // Initialize page tracking
  usePageTracker();
  
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public authentication routes (no layout) */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      
      {/* Public routes with layout */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/upgrade" element={<Upgrade />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
      </Route>
      <Route path="/dashboard" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="links">
          <Route index element={<LinkList />} />
          <Route path="create" element={<LinkCreate />} />
          <Route path=":id" element={<LinkDetails />} />
        </Route>
        <Route path="conversions" element={<ConversionList />} />
        <Route path="export" element={<ExportData />} />
        <Route path="subscription">
          <Route index element={<SubscriptionPage />} />
          <Route path="success" element={<SubscriptionSuccess />} />
          <Route path="cancel" element={<SubscriptionCancel />} />
        </Route>
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="account" element={<AccountPage />} />
        <Route path="pro-features" element={<ProFeatures />} />
      </Route>
      
      {/* Not Found route */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <AnalyticsProvider>
        <AppRoutes />
        <CookieConsent />
      </AnalyticsProvider>
    </AuthProvider>
  );
}

export default App;
